import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import * as moment from 'moment';
import { combineLatest, debounce, Observable, timer } from 'rxjs';
import { DaysOnHandRequired, FrequenciesRequired, ReorderDetails } from 'src/app/core/models';
import {
  SetProductFrequenciesUserData,
  SetProductItemsDohUserData,
  SetProductItemsOuDohUserData,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import {
  ProductFrequenciesUserData,
  ProductItemDohUserData,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import {
  decimalValidator,
  integerValidator,
  minExclusiveValidator,
  validateAllFormFields,
} from 'src/app/core/utils/validators';

const DOH_INTEGER_VALIDATORS = [Validators.required, integerValidator(), Validators.min(0)];
const FREQ_INTEGER_VALIDATORS = [Validators.required, decimalValidator(), minExclusiveValidator(0)];
const FREQ_DECIMAL_VALIDATORS = [Validators.required, decimalValidator(), minExclusiveValidator(0)];

@Component({
  selector: 'app-insurance-requirements',
  templateUrl: './insurance-requirements.component.html',
  styleUrls: ['./insurance-requirements.component.scss'],
})
export class InsuranceRequirementsComponent implements OnInit {
  @Select(OrderWizardDataState.reorder) reorder$!: Observable<ReorderDetails | null>;
  @Select(OrderWizardDataState.daysOnHandRequired) daysOnHandRequired$!: Observable<DaysOnHandRequired[]>;
  @Select(OrderWizardDataState.frequenciesRequired) frequenciesRequired$!: Observable<FrequenciesRequired>;
  @Select(OrderWizardDataState.isOuShipment) isOuShipment$!: Observable<boolean>;
  @Select(OrderWizardDataState.productItemDohUserData)
  productItemDohUserData$!: Observable<ProductItemDohUserData[] | null>;
  @Select(OrderWizardDataState.productItemOuDohUserData) productItemOuDohUserData$!: Observable<number | null>;
  @Select(OrderWizardDataState.productFrequenciesUserData)
  productFrequenciesUserData$!: Observable<ProductFrequenciesUserData | null>;

  initialDaysOnHand: DaysOnHandRequired[] = [];
  initialOuDaysOnHand: number | null = null;

  dohForm?: FormArray;
  ouDaysOnHandForm?: FormGroup;
  frequenciesForm?: FormGroup;

  constructor(private store: Store, private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.initiateDohForm();
    this.initiateFrequenciesForm();
  }

  initiateDohForm() {
    combineLatest([
      this.daysOnHandRequired$,
      this.productItemDohUserData$,
      this.isOuShipment$,
      this.productItemOuDohUserData$,
      this.reorder$,
    ]).subscribe(([daysOnHandRequired, productItemDohUserData, isOuShipment, productItemOuDohUserData, reorder]) => {
      if (isOuShipment) {
        const reorderTargetedShipDate = reorder?.targetedShipDate;
        const estimatedDaysOnHand = reorderTargetedShipDate
          ? Math.max(moment(reorderTargetedShipDate).startOf('day').diff(moment().startOf('day'), 'days'), 0)
          : 0;
        const ouDaysOnHand = productItemOuDohUserData === null ? estimatedDaysOnHand : productItemOuDohUserData;

        if (this.initialOuDaysOnHand === ouDaysOnHand) return;

        this.initialOuDaysOnHand = ouDaysOnHand;

        this.ouDaysOnHandForm = this.formBuilder.group({
          ouDaysOnHand: [ouDaysOnHand, DOH_INTEGER_VALIDATORS],
        });

        this.setDohFormListener();
        return;
      }

      if (!daysOnHandRequired) return;

      const updatedDaysOnHand = daysOnHandRequired.filter((dohRequirement) => dohRequirement.daysOnHandRequired);

      // TODO Remove this line after updating the store
      // This was added to prevent against redundant re-evaluations of reorder$
      if (isEqual(this.initialDaysOnHand, updatedDaysOnHand)) return;

      this.initialDaysOnHand = updatedDaysOnHand;

      if (!productItemDohUserData) {
        this.dohForm = new FormArray(
          updatedDaysOnHand.map((dohRequirement) =>
            this.formBuilder.group({
              itemId: [dohRequirement.itemId],
              doh: [dohRequirement.estimatedDaysOnHand, DOH_INTEGER_VALIDATORS],
            })
          )
        );
      } else {
        this.dohForm = new FormArray(
          productItemDohUserData.map((itemDohUserData) =>
            this.formBuilder.group({
              itemId: [itemDohUserData.itemId],
              doh: [itemDohUserData.doh, DOH_INTEGER_VALIDATORS],
            })
          )
        );
      }

      this.setDohFormListener();
    });
  }

  initiateFrequenciesForm() {
    combineLatest([this.frequenciesRequired$, this.productFrequenciesUserData$]).subscribe(
      ([frequenciesRequired, frequenciesUserData]) => {
        if (!frequenciesRequired) return;

        if (!frequenciesUserData) {
          this.frequenciesForm = new FormGroup({
            cgmSiteChgFreq: new FormControl(
              null,
              frequenciesRequired.cgmSiteChgFreqRequired ? FREQ_DECIMAL_VALIDATORS : []
            ),
            pumpSiteChgFreq: new FormControl(
              null,
              frequenciesRequired.pumpSiteChgFreqRequired ? FREQ_DECIMAL_VALIDATORS : []
            ),
            patientTestFreq: new FormControl(
              null,
              frequenciesRequired.patientTestFreqRequired ? FREQ_INTEGER_VALIDATORS : []
            ),
          });
        } else {
          this.frequenciesForm = new FormGroup({
            cgmSiteChgFreq: new FormControl(
              frequenciesUserData.cgmSiteChgFreq,
              frequenciesRequired.cgmSiteChgFreqRequired ? FREQ_DECIMAL_VALIDATORS : []
            ),
            pumpSiteChgFreq: new FormControl(
              frequenciesUserData.pumpSiteChgFreq,
              frequenciesRequired.pumpSiteChgFreqRequired ? FREQ_DECIMAL_VALIDATORS : []
            ),
            patientTestFreq: new FormControl(
              frequenciesUserData.patientTestFreq,
              frequenciesRequired.patientTestFreqRequired ? FREQ_INTEGER_VALIDATORS : []
            ),
          });
        }

        this.setFrequenciesFormListener();
      }
    );
  }

  setDohFormListener() {
    if (this.dohForm) {
      this.dohForm.valueChanges.pipe(debounce(() => timer(100))).subscribe((values) => {
        this.store.dispatch(new SetProductItemsDohUserData(values, this.dohForm!.valid));
      });
    } else if (this.ouDaysOnHandForm) {
      this.ouDaysOnHandForm
        .get('ouDaysOnHand')
        ?.valueChanges.pipe(debounce(() => timer(100)))
        .subscribe((value) => {
          this.store.dispatch(new SetProductItemsOuDohUserData(value));
        });
    }
  }

  setFrequenciesFormListener() {
    if (!this.frequenciesForm) return;

    this.frequenciesForm.valueChanges.subscribe((values) => {
      this.store.dispatch(new SetProductFrequenciesUserData(values));
    });
  }

  asFormControl(a: AbstractControl) {
    return a as FormControl;
  }

  onSubmit(): boolean {
    if (
      (this.ouDaysOnHandForm && !this.ouDaysOnHandForm.valid) ||
      (this.dohForm && !this.dohForm.valid) ||
      (this.frequenciesForm && !this.frequenciesForm.valid)
    ) {
      this.ouDaysOnHandForm && validateAllFormFields(this.ouDaysOnHandForm);
      this.dohForm && validateAllFormFields(this.dohForm);
      this.frequenciesForm && validateAllFormFields(this.frequenciesForm);
      return false;
    }

    return true;
  }
}
